import React, { useEffect, useState } from "react";
import {
    PhoneOutlined,
    UserOutlined,
    DollarOutlined,
    CalendarOutlined,
    TagOutlined,
    IdcardOutlined,
    DownOutlined,
    SubnodeOutlined,
} from "@ant-design/icons";
import avatar from "../../assets/img/avatars/default.png";
import PromoCardLayer from "../../layers/PromoCardLayer/PromoCardLayer";
import GemsLayer from "../../layers/GemsLayer/GemsLayer";
import promosApi from "../../api/promosApi";
import SubscribePromoModal from "./SubscribePromoModal";

function cn(...c) {
    return c.filter(Boolean).join(" ");
}

const accordionBtn =
    "flex w-full items-center justify-between rounded-xl border border-white/10 bg-white/[0.05] px-4 py-2.5 text-left text-[13px] font-medium text-white/85 transition-colors hover:border-white/[0.14] hover:bg-white/[0.08]";

const UserDetails = ({ user }) => {
    const [promos, setPromos] = useState([]);
    const [promosLoading, setPromosLoading] = useState(true);
    const defaultAvatar = avatar;
    const [activeAccordion, setActiveAccordion] = useState("info");
    const [isSubscribeModalOpen, setIsSubscribeModalOpen] = useState(false);

    const displayName =
        user?.name?.trim() ||
        [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim() ||
        "User";

    const toggleAccordion = (accordion) => {
        setActiveAccordion((prev) => (prev === accordion ? null : accordion));
    };

    const promosHandle = async () => {
        try {
            setPromosLoading(true);
            const response = await promosApi.userPromos.getPromos(user.id);
            const validPromos = response.filter((p) => p && p.promo && p.promo.id);
            setPromos(validPromos);
        } catch (e) {
            console.error("Failed to fetch promos", e);
            setPromos([]);
        } finally {
            setPromosLoading(false);
        }
    };

    useEffect(() => {
        promosHandle();
    }, [user.id]);

    if (!user) {
        return <p className="py-8 text-center text-sm text-white/45">No user data provided</p>;
    }

    return (
        <div className="mx-auto flex w-full max-w-full flex-col items-stretch px-3 py-4 font-poppins text-white sm:px-4">
            <div className="mb-6 flex flex-col items-center gap-3">
                <div className="relative">
                    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-emerald-400/20 to-transparent blur-md" />
                    <img
                        src={user.avatar || defaultAvatar}
                        alt=""
                        className="relative h-20 w-20 rounded-full border-2 border-white/15 object-cover shadow-[0_12px_40px_-16px_rgba(0,0,0,0.8)]"
                    />
                </div>
                <div className="text-center">
                    <h2 className="text-lg font-semibold tracking-tight text-white/95 sm:text-xl">{displayName}</h2>
                    <p className="text-[13px] text-white/45">{user.location || "—"}</p>
                </div>
            </div>

            <button type="button" className={accordionBtn} onClick={() => toggleAccordion("info")}>
                <span>User information</span>
                <DownOutlined
                    className={cn(
                        "text-base text-white/50 transition-transform duration-300",
                        activeAccordion === "info" ? "rotate-180" : "rotate-0"
                    )}
                />
            </button>

            <div
                className={cn(
                    "w-full overflow-hidden rounded-xl border transition-all duration-300 ease-out",
                    activeAccordion === "info"
                        ? "mt-3 max-h-[600px] border-white/10 bg-white/[0.04]"
                        : "max-h-0 border-0 p-0"
                )}
            >
                <div className="flex w-full flex-col gap-3 p-4">
                    <div className="flex flex-row items-center gap-3">
                        <IdcardOutlined className="text-lg text-sky-400/90" />
                        <p className="m-0 text-[13px] leading-snug text-white/80">
                            <span className="font-medium text-white/45">ID: </span>
                            {user.id}
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <PhoneOutlined className="text-lg text-emerald-400/90" />
                        <p className="m-0 text-[13px] text-white/80">
                            <span className="font-medium text-white/45">Phone: </span>
                            {user.phone || <span className="text-white/35">***</span>}
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <UserOutlined className="text-lg text-sky-400/90" />
                        <p className="m-0 text-[13px] text-white/80">
                            <span className="font-medium text-white/45">Telegram: </span>
                            {user.telegramId ? (
                                <a
                                    href={`https://t.me/${String(user.telegramId).replace("@", "")}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-emerald-300/90 hover:text-emerald-200 hover:underline"
                                >
                                    {user.telegramId}
                                </a>
                            ) : (
                                <span className="text-white/35">N/A</span>
                            )}
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <CalendarOutlined className="text-lg text-amber-300/85" />
                        <p className="m-0 text-[13px] text-white/80">
                            <span className="font-medium text-white/45">Auth date: </span>
                            {user.createdAt ? (
                                new Date(user.createdAt).toLocaleString()
                            ) : (
                                <span className="text-white/35">N/A</span>
                            )}
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <TagOutlined className="text-lg text-violet-400/90" />
                        <div>
                            <span className="text-[13px] font-medium text-white/45">Statuses: </span>
                            {user.statuses && user.statuses.length > 0 ? (
                                user.statuses.map((status) => (
                                    <span
                                        key={status}
                                        className={cn(
                                            "mr-2 inline-block rounded-lg border px-2 py-0.5 text-[12px] font-medium",
                                            status === "Promoter"
                                                ? "border-sky-400/30 bg-sky-500/15 text-sky-200"
                                                : status === "Member"
                                                  ? "border-emerald-400/30 bg-emerald-500/15 text-emerald-200"
                                                  : "border-white/10 bg-white/10 text-white/75"
                                        )}
                                    >
                                        {status}
                                    </span>
                                ))
                            ) : (
                                <span className="text-white/35">N/A</span>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <DollarOutlined className="text-lg text-emerald-400/90" />
                        <p className="m-0 text-[13px] text-white/80">
                            <span className="font-medium text-white/45">Total deposit: </span>
                            {user.totalDeposit ?? 0}
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <DollarOutlined className="text-lg text-amber-300/85" />
                        <p className="m-0 text-[13px] text-white/80">
                            <span className="font-medium text-white/45">Balance: </span>
                            {user.balance ?? 0}
                        </p>
                    </div>
                </div>
            </div>

            <div className="mt-5 flex w-full flex-col gap-2 sm:flex-row sm:items-stretch">
                <button type="button" className={cn(accordionBtn, "sm:flex-1")} onClick={() => toggleAccordion("promos")}>
                    <span>Member promos</span>
                    <DownOutlined
                        className={cn(
                            "text-base text-white/50 transition-transform duration-300",
                            activeAccordion === "promos" ? "rotate-180" : "rotate-0"
                        )}
                    />
                </button>
                <button
                    type="button"
                    onClick={() => setIsSubscribeModalOpen(true)}
                    className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl border border-emerald-400/35 bg-emerald-500/15 px-4 py-2.5 text-[13px] font-medium text-emerald-100 transition-colors hover:border-emerald-400/50 hover:bg-emerald-500/25"
                >
                    <SubnodeOutlined />
                    Подписать
                </button>
            </div>

            <div
                className={cn(
                    "overflow-hidden rounded-xl border transition-all duration-300 ease-out",
                    activeAccordion === "promos"
                        ? "mt-3 max-h-[600px] border-white/10 bg-white/[0.04]"
                        : "max-h-0 border-0 p-0"
                )}
            >
                <div className="flex w-full flex-col p-2">
                    {promos.length === 0 && !promosLoading ? (
                        <p className="py-6 text-center text-[13px] text-white/45">No active promos</p>
                    ) : (
                        <PromoCardLayer userId={user.id} promos={promos} isLoading={promosLoading} />
                    )}
                </div>
            </div>

            <button type="button" className={cn(accordionBtn, "mt-5")} onClick={() => toggleAccordion("gems")}>
                <span>Gems</span>
                <DownOutlined
                    className={cn(
                        "text-base text-white/50 transition-transform duration-300",
                        activeAccordion === "gems" ? "rotate-180" : "rotate-0"
                    )}
                />
            </button>

            <div
                className={cn(
                    "overflow-hidden rounded-xl border transition-all duration-300 ease-out",
                    activeAccordion === "gems"
                        ? "mt-3 max-h-[300px] border-white/10 bg-white/[0.04] p-2"
                        : "max-h-0 border-0 p-0"
                )}
            >
                <div className="flex w-full flex-col">
                    <GemsLayer />
                </div>
            </div>

            <SubscribePromoModal
                visible={isSubscribeModalOpen}
                onClose={() => setIsSubscribeModalOpen(false)}
                userId={user.id}
                onSubscribed={promosHandle}
            />
        </div>
    );
};

export default UserDetails;
